import { useCallback } from 'react';

import {
    buildAgentTraceChip,
    buildOrganizationChip,
} from '@apps/web/features/admin_agent_dock/chip_context_providers';
import { type ChipHandle } from '@apps/web/features/admin_agent_dock/chip_registry';
import { useAdminMode } from '@apps/web/lib/admin_mode_store';
import { getApiClient } from '@apps/web/lib/api_client';

// On-click chip resolvers for the "+ add to chat" affordances. Each
// fetches the kind's fast summary (the same read the detail page's
// route auto-chip uses) so every row-sourced chip carries the full
// structured facts block promised by the chip registry. If the summary
// is unavailable (no linked customer account, endpoint error, mismatch
// on identity check) the resolver rejects so the caller can retry —
// persisting a `facts: null` chip would silently poison the chat
// context AND block later successful clicks via chip dedupe.

export function useResolveOrganizationChip(): (account: {
    // Core account id whose summary carries the organization
    // context. Null when no linked customer account exists — the
    // resolver rejects in that case so the click stays retryable.
    readonly accountId: string | null;
    readonly organizationId: string;
    readonly fallbackLabel: string;
}) => Promise<ChipHandle> {
    return useCallback(async ({ accountId, organizationId }) => {
        if (accountId === null) {
            throw new Error(
                'Organization has no linked customer account; structured context unavailable',
            );
        }
        const response = await getApiClient().get({
            url: '/admin/accounts/{account_id}/summary',
            pathParams: { account_id: accountId },
        });
        const context =
            response.account_page_summary.organization_context ?? null;
        // Identity check: an account linked to multiple
        // organizations reports one context — only trust it
        // when it describes the organization being added.
        if (context === null || context.organization_id !== organizationId) {
            throw new Error(
                'Organization context unavailable for this organization',
            );
        }
        return buildOrganizationChip(context);
    }, []);
}

export function useResolveAgentTraceChip(): (trace: {
    readonly conversationId: string;
    readonly createdAt: string;
}) => Promise<ChipHandle> {
    const adminMode = useAdminMode();
    return useCallback(
        async ({ conversationId }) => {
            const summary = await getApiClient().get({
                url: '/admin/agent-traces/{conversation_id}/summary',
                pathParams: { conversation_id: conversationId },
                mode: adminMode,
            });
            return buildAgentTraceChip(conversationId, summary);
        },
        [adminMode],
    );
}
